import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';

const ContactUs = () => {
  const { user, userProfile, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    userType: '',
    category: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);

  const userTypes = [
    { value: 'student', label: 'Student' },
    { value: 'educator', label: 'Educator' },
    { value: 'administrator', label: 'Administrator' },
    { value: 'parent', label: 'Parent/Guardian' },
    { value: 'other', label: 'Other' }
  ];

  const categories = [
    { value: 'account', label: 'Account Issues' },
    { value: 'technical', label: 'Technical Problems' },
    { value: 'content', label: 'Content Questions' },
    { value: 'partnership', label: 'Partnership Inquiry' },
    { value: 'billing', label: 'Billing & Subscriptions' },
    { value: 'feedback', label: 'Feedback & Suggestions' },
    { value: 'other', label: 'Other' }
  ];

  const contactMethods = [
    {
      icon: 'Mail',
      title: 'Email Support',
      value: 'support@bardplay.com',
      description: 'Response within 24 hours',
      action: 'mailto:support@bardplay.com'
    },
    {
      icon: 'MessageSquare',
      title: 'Technical Issues',
      value: 'tech@bardplay.com',
      description: 'Priority support for urgent issues',
      action: 'mailto:tech@bardplay.com'
    },
    {
      icon: 'Users',
      title: 'Partnership Inquiries',
      value: 'partnerships@bardplay.com',
      description: 'For schools and institutions',
      action: 'mailto:partnerships@bardplay.com'
    }
  ];

  const faqs = [
    {
      question: 'How do I reset my password?',
      answer: 'You can reset your password by clicking the "Forgot Password" link on the login page. Follow the instructions sent to your email to create a new password.'
    },
    {
      question: 'Is BardPlay suitable for all grade levels?',
      answer: 'BardPlay is designed for middle school through high school students studying Shakespeare. Our adaptive learning system adjusts to different skill levels and learning paces.'
    },
    {
      question: 'Can educators track student progress?',
      answer: 'Yes! Educators have access to comprehensive analytics dashboards showing individual student progress, quiz scores, completion rates, and engagement metrics.'
    },
    {
      question: 'What is your refund policy?',
      answer: 'We offer a 30-day money-back guarantee for all subscriptions. If you\'re not satisfied within the first 30 days, contact us for a full refund.'
    },
    {
      question: 'Do you offer institutional pricing?',
      answer: 'Yes, we offer special pricing for schools and educational institutions. Contact our partnerships team at partnerships@bardplay.com for custom quotes and volume discounts.'
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e?.target || {};
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitSuccess(true);
      setFormData({ name: '', email: '', userType: '', category: '', message: '' });
      
      setTimeout(() => setSubmitSuccess(false), 5000);
    }, 1500);
  };

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  return (
    <>
      <Helmet>
        <title>Contact Us - BardPlay</title>
        <meta name="description" content="Get in touch with the BardPlay support team for help with account issues, technical problems, partnerships, or general inquiries." />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-background via-surface-dark to-background">
        <RoleBasedNavigation 
          user={{
            ...user,
            role: userProfile?.role,
            full_name: userProfile?.full_name,
            email: user?.email
          }} 
          isAuthenticated={isAuthenticated} 
        />
        
        <main className="main-content pt-24 pb-16">
          {/* Header */}
          <div className="max-w-screen-xl mx-auto px-6 lg:px-8 mb-12">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Contact Us</h1>
              <p className="text-lg text-white/70 max-w-2xl mx-auto">
                Have questions or need assistance? We're here to help! Choose the best way to reach our team.
              </p>
            </div>
          </div>

          <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Contact Form */}
              <div className="lg:col-span-2">
                <div className="bg-surface-dark/50 backdrop-blur-sm rounded-xl p-6 md:p-8 border border-white/10">
                  <h2 className="text-2xl font-bold text-white mb-6">Send Us a Message</h2>
                  
                  {submitSuccess && (
                    <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg flex items-start gap-3">
                      <Icon name="CheckCircle" size={24} className="text-green-500 flex-shrink-0" />
                      <div>
                        <p className="text-green-500 font-semibold">Message Sent Successfully!</p>
                        <p className="text-white/70 text-sm mt-1">We'll get back to you within 24 hours.</p>
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-white font-medium mb-2">Name *</label>
                        <Input
                          type="text"
                          name="name"
                          value={formData?.name}
                          onChange={handleInputChange}
                          placeholder="Your full name"
                          required
                          className="w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-white font-medium mb-2">Email *</label>
                        <Input
                          type="email"
                          name="email"
                          value={formData?.email}
                          onChange={handleInputChange}
                          placeholder="your.email@example.com"
                          required
                          className="w-full"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-white font-medium mb-2">I am a *</label>
                        <Select
                          name="userType"
                          value={formData?.userType}
                          onChange={handleInputChange}
                          required
                          className="w-full"
                        >
                          <option value="">Select user type</option>
                          {userTypes?.map(type => (
                            <option key={type?.value} value={type?.value}>{type?.label}</option>
                          ))}
                        </Select>
                      </div>
                      <div>
                        <label className="block text-white font-medium mb-2">Inquiry Type *</label>
                        <Select
                          name="category"
                          value={formData?.category}
                          onChange={handleInputChange}
                          required
                          className="w-full"
                        >
                          <option value="">Select category</option>
                          {categories?.map(cat => (
                            <option key={cat?.value} value={cat?.value}>{cat?.label}</option>
                          ))}
                        </Select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-white font-medium mb-2">Message *</label>
                      <textarea
                        name="message"
                        value={formData?.message}
                        onChange={handleInputChange}
                        placeholder="Tell us how we can help you..."
                        required
                        rows={6}
                        className="w-full px-4 py-3 bg-background/50 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-primary transition-colors"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full md:w-auto px-8"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <Icon name="Loader" size={20} className="animate-spin" />
                          Sending...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Icon name="Send" size={20} />
                          Send Message
                        </span>
                      )}
                    </Button>
                  </form>
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-6">
                {/* Contact Methods */}
                <div className="bg-surface-dark/50 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                  <h3 className="text-xl font-bold text-white mb-4">Other Ways to Reach Us</h3>
                  <div className="space-y-4">
                    {contactMethods?.map((method, index) => (
                      <a
                        key={index}
                        href={method?.action}
                        className="block p-4 bg-background/30 rounded-lg hover:bg-background/50 transition-colors border border-white/10"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                            <Icon name={method?.icon} size={20} className="text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-white font-semibold mb-1">{method?.title}</h4>
                            <p className="text-primary text-sm mb-1 truncate">{method?.value}</p>
                            <p className="text-white/60 text-xs">{method?.description}</p>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>

                {/* Support Hours */}
                <div className="bg-surface-dark/50 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Icon name="Clock" size={20} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white mb-2">Support Hours</h3>
                      <div className="space-y-2 text-sm">
                        <p className="text-white/70">
                          <span className="font-semibold text-white">Monday - Friday:</span> 8:00 AM - 8:00 PM EST
                        </p>
                        <p className="text-white/70">
                          <span className="font-semibold text-white">Saturday:</span> 10:00 AM - 4:00 PM EST
                        </p>
                        <p className="text-white/70">
                          <span className="font-semibold text-white">Sunday:</span> Closed
                        </p>
                      </div>
                      <p className="text-white/60 text-xs mt-3">
                        *Emergency technical support available 24/7 for critical issues
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="mt-16">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-white mb-3">Frequently Asked Questions</h2>
                <p className="text-white/70">Find quick answers to common questions</p>
              </div>

              <div className="max-w-3xl mx-auto bg-surface-dark/50 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden">
                {faqs?.map((faq, index) => (
                  <div key={index} className={`border-b border-white/10 ${index === faqs?.length - 1 ? 'border-b-0' : ''}`}>
                    <button
                      onClick={() => toggleFaq(index)}
                      className="w-full px-6 py-4 flex items-center justify-between hover:bg-white/5 transition-colors duration-200 text-left"
                    >
                      <span className="text-white font-semibold pr-4">{faq?.question}</span>
                      <Icon 
                        name={expandedFaq === index ? 'ChevronUp' : 'ChevronDown'} 
                        size={20} 
                        className="text-white/60 flex-shrink-0"
                      />
                    </button>
                    {expandedFaq === index && (
                      <div className="px-6 pb-4">
                        <p className="text-white/70 leading-relaxed">{faq?.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default ContactUs;